import { Helmet } from 'react-helmet-async';

interface MetaTagsProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  twitterSite?: string;
  structuredData?: Record<string, any>;
  alternateLanguages?: { lang: string; url: string }[];
}

const defaultProps: MetaTagsProps = {
  title: 'Rion Wildlife Safaris | Premium African Safari Experiences',
  description: 'Experience unforgettable safari adventures in Namibia, Botswana and Victoria Falls with Rion Wildlife Safaris. Led by Professor Rion Haraeb with 20+ years of expertise. Bilingual English/German tours.',
  ogType: 'website',
  ogImage: '/images/safari-og-image.jpg', // Update this with your actual image path
  twitterCard: 'summary_large_image',
  twitterSite: '@rionwildlifesafaris', // Update with actual Twitter handle
  alternateLanguages: [
    { lang: 'en', url: 'https://rionwildlifesafaris.com/' }, // Update with your actual domain
    { lang: 'de', url: 'https://rionwildlifesafaris.com/de/' }, // Update with your actual domain
  ]
};

const MetaTags: React.FC<MetaTagsProps> = (props) => {
  const {
    title,
    description,
    canonicalUrl,
    ogType,
    ogImage,
    twitterCard,
    twitterSite,
    structuredData,
    alternateLanguages,
  } = { ...defaultProps, ...props };

  // Base URL for constructing absolute URLs
  const baseUrl = 'https://rionwildlifesafaris.com'; // Update with your actual domain
  const fullOgImageUrl = ogImage?.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Favicon Tags */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {fullOgImageUrl && <meta property="og:image" content={fullOgImageUrl} />}
      <meta property="og:site_name" content="Rion Wildlife Safaris" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="de_DE" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {fullOgImageUrl && <meta name="twitter:image" content={fullOgImageUrl} />}
      
      {/* Geo Tags */}
      <meta name="geo.region" content="NA" /> {/* Namibia */}
      <meta name="geo.placename" content="Windhoek" />
      
      {/* Mobile App Tags */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="theme-color" content="#C05B2A" /> {/* Using the primary brand color */}
      
      {/* Alternate Language Tags */}
      {alternateLanguages?.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default MetaTags; 